import { useEffect, useState } from "react";
import { Button, Grid, TextField, Typography } from "@mui/material";
import { ClientProps } from "../../interfaces/interfaces";
import styles from "../components.module.sass";
import { TitleComponent } from "../ui/TitleComponent";

export const SUGGESTED_REPORT_TYPES = ["Performance", "Refurbishment"];

export interface ReportFormValues {
  client: ClientProps;
  reportType: string;
  file?: File | null;
}

interface Props {
  clientOptions: ClientProps[];
  initialValues?: Partial<ReportFormValues>;
  onCancel: () => void;
  onSubmit: (values: ReportFormValues) => void;
}

const emptyClient = { id: 0, name: "" };

export const ReportForm = ({
  clientOptions,
  initialValues,
  onCancel,
  onSubmit,
}: Props) => {
  const [values, setValues] = useState<ReportFormValues>({
    client: emptyClient,
    reportType: "",
    file: null,
  });

  useEffect(() => {
    if (!initialValues) return;
    setValues((current) => ({
      ...current,
      ...initialValues,
      file: null,
    }));
  }, [initialValues]);

  const canSave = Boolean(
    values.client?.id &&
      values.reportType.trim() &&
      values.file,
  );

  return (
    <div className={styles.customToolForm}>
      <TitleComponent title={initialValues ? "Regenerate report" : "Generate report"} />
      <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
        Upload one Excel workbook. OSI will build the dashboard, title, and
        reporting period automatically.
      </Typography>
      <Grid container spacing={2}>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            label="Client"
            onChange={(event) => {
              const client =
                clientOptions.find(
                  (option) => String(option.id) === event.target.value,
                ) ?? emptyClient;
              setValues((current) => ({ ...current, client }));
            }}
            select
            SelectProps={{ native: true }}
            value={values.client?.id || ""}
          >
            <option value="" disabled>
              Select a client...
            </option>
            {clientOptions.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item lg={12} xs={12}>
          <TextField
            className={styles.formFieldModalCustom}
            label="Report type"
            select
            SelectProps={{ native: true }}
            value={values.reportType}
            onChange={(event) => {
              setValues((current) => ({
                ...current,
                reportType: event.target.value,
              }));
            }}
          >
            <option value="" disabled>
              Select a report type...
            </option>
            {SUGGESTED_REPORT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </TextField>
        </Grid>
        <Grid item lg={12} xs={12}>
          <Typography component="label" display="block" fontWeight={700} mb={0.75} variant="body2">
            Excel workbook
          </Typography>
          <input
            accept=".xlsx,.xlsm,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className={styles.formFieldModalCustom}
            name="file"
            onChange={(event) =>
              setValues((current) => ({
                ...current,
                file: event.target.files?.[0] ?? null,
              }))
            }
            type="file"
          />
          <Typography color="text.secondary" mt={0.75} variant="caption">
            Performance expects a “Test Report1” sheet. Refurbishment expects
            an “InputData” sheet.
          </Typography>
        </Grid>
      </Grid>
      <div className={styles.buttonGral}>
        <Button onClick={onCancel} variant="contained">
          Cancel
        </Button>
        <Button
          disabled={!canSave}
          onClick={() => onSubmit(values)}
          variant="contained"
        >
          Generate dashboard
        </Button>
      </div>
    </div>
  );
};
